import { get, groupBy, intersectionBy, isEmpty, find, take, slice, findLast } from 'lodash';
import { ACCOMMODATION_COMFORT_ORDER } from 'Utils/accommodation/accommodation.constants';
type Data = {
  id: string;
  [key: string]: any;
};

const defaultMatch = (a: any, b: any): boolean => a === b;

function _getMatcher(filter: Filter): (arg0: any, arg1: any) => boolean {
  return filter.match || defaultMatch;
}

function _matchFilter(data: Data): (filter: Filter) => boolean {
  return (filter: Filter): boolean => {
    const dataValue = get(data, filter.path);
    const filterValue = filter.value;
    return _getMatcher(filter)(dataValue, filterValue);
  };
}

function _toggleFilter(filters: Array<Filter>): (arg0: Filter) => Array<Filter> {
  return (toggledFilter: Filter) => {
    return filters.map((filter) => {
      const shouldToggleFilter =
        filter.path === toggledFilter.path && filter.value === toggledFilter.value;
      return { ...filter, selected: shouldToggleFilter ? !filter.selected : filter.selected };
    });
  };
}

function _filterDataSet(
  dataSet: Array<Data>,
  filters: Array<Filter>,
  condition: (arg0: Filter) => boolean = () => true,
): Array<Data> {
  const activeFilters = filters.filter((f) => condition(f) === true);
  return dataSet.filter((data) => {
    const match = _matchFilter(data);

    return isEmpty(activeFilters) || activeFilters.some((activeFilter) => match(activeFilter));
  });
}

function _removeUnusedFilters(filters: Array<Filter>): Array<Filter> {
  return filters.map((filter) => {
    return filter.selected === true && filter.matchedOccurrences === 0
      ? { ...filter, selected: false }
      : filter;
  });
}

function filterDataSetWithSelectedAndMatchedFilters(
  dataSet: Array<Data>,
  filters: Array<Filter>,
): Array<Data> {
  return _filterDataSet(
    dataSet,
    filters,
    (filter) => filter.selected && get(filter, 'matchedOccurrences', 0) > 0,
  );
}

function getDataSetIfFilterWasSelected(dataSet: Array<Data>, filter: Filter): Array<Data> {
  return _filterDataSet(dataSet, [filter]);
}

function getFiltersWithMatchedOccurrences(
  dataSet: Array<Data>,
): (arg0: Array<Filter>) => Array<Filter> {
  return (filters: Array<Filter>) => {
    // First, we group all filters by their path
    const filtersByCategory = groupBy(filters, 'category');
    return filters.map((filter) => {
      // Then, for every filter, we get all data objects matching all active filters in all other categories
      const matchedResultsInOtherCategories = Object.keys(filtersByCategory)
        .filter((category) => category !== filter.category)
        .map((filterKey) => {
          return filterDataSetWithSelectedAndMatchedFilters(
            dataSet,
            get(filtersByCategory, filterKey),
          );
        });
      // Then, we activate the current filter (whether its current activation state) and get all data objects matching only this filter
      const matchedResultsForCurrentFilter = getDataSetIfFilterWasSelected(dataSet, filter);
      const matchResults =
        intersectionBy(matchedResultsForCurrentFilter, matchedResultsForCurrentFilter, 'id') || [];
      // Finally, The intersection gives us the list of results matching all our active filters
      // prettier-ignore
      const matchedOccurrences = (
      intersectionBy(...matchedResultsInOtherCategories, matchResults, 'id') || []).length;
      return { ...filter, matchedOccurrences };
    });
  };
}

function _activateFilter(filters: Array<Filter>, valuesToActivate: Array<string>): Array<Filter> {
  return filters.map((filter) => {
    const matchedOccurrences = get(filter, 'matchedOccurrences', 0);

    if (matchedOccurrences > 0 && valuesToActivate.includes(filter.value)) {
      return { ...filter, selected: true };
    }

    return filter;
  });
}

function getPreselectedFilters(filters: Array<Filter>, selectedProposal: Proposal) {
  const accommodationFilters = filters.filter(
    (f) => f.category === 'accommodations' && get(f, 'matchedOccurrences', 0) > 0,
  );
  // categoryId is the comfort level of the room, used as value for comfort filters
  const selectedProposalComfort = get(selectedProposal, 'accommodations[0].categoryId');
  const selectedProposalComfortOrder = ACCOMMODATION_COMFORT_ORDER.indexOf(selectedProposalComfort);
  // at first, activate filters equal to selected proposal comfort level
  // and those with higher comfort level
  const higherComfortCodes = slice(ACCOMMODATION_COMFORT_ORDER, selectedProposalComfortOrder);
  const filtersToActivate = higherComfortCodes.filter((code) =>
    find(accommodationFilters, {
      value: code,
    }),
  );

  // if no equal or higher comfort level filter found,
  // activate the one below the selected proposal comfort level
  if (filtersToActivate.length === 0) {
    const lowerComfortCodes = take(ACCOMMODATION_COMFORT_ORDER, selectedProposalComfortOrder);
    // use findLast to start from the end of the array
    // and get the highest comfort level possible
    const codeToActivate = findLast(lowerComfortCodes, (code) =>
      find(accommodationFilters, {
        value: code,
      }),
    );
    codeToActivate && filtersToActivate.push(codeToActivate);
  }

  switch (selectedProposalComfort) {
    // if selected proposal comfort level is club, club seaside view
    // or superior (default state), do not activate any filter
    case '000017': // club
    // falls through
    case '000048': // club seaside view
    // falls through
    case '000061': // superior
      return filters;

    default:
      return _activateFilter(filters, filtersToActivate);
  }
}

function updateFilters(dataSet: Array<Data>): (arg0: Array<Filter>) => Array<Filter> {
  return (filters) => {
    const updateMatchOccurrences = getFiltersWithMatchedOccurrences(dataSet);
    const updatedOccurrencesFilters = updateMatchOccurrences(filters);
    return _removeUnusedFilters(updatedOccurrencesFilters);
  };
}

function toggleFilter(dataSet: Array<Data>): (arg0: Array<Filter>, arg1: Filter) => Array<Filter> {
  return (filters, toggledFilter) => {
    const updatedToggleFilters = _toggleFilter(filters)(toggledFilter);

    return updateFilters(dataSet)(updatedToggleFilters);
  };
}

function filterDataSet(dataSet: Array<Data>, filters?: Array<Filter>): Array<Data> {
  if (!filters) {
    return dataSet;
  }

  const filtersByCategory = groupBy(filters, 'category');
  return Object.keys(filtersByCategory).reduce(
    (filteredResults, category) =>
      filterDataSetWithSelectedAndMatchedFilters(filteredResults, filtersByCategory[category]),
    dataSet,
  );
}

export {
  filterDataSet,
  getFiltersWithMatchedOccurrences,
  updateFilters,
  toggleFilter,
  getPreselectedFilters,
};
